import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { geciciTaslakHasContent } from '../utils/serviceRecordWizardState';

const STORAGE_KEY = 'hlm-sk-gecici-taslak';

const ServiceRecordWizardDraftContext = createContext(null);

function loadFromSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return geciciTaslakHasContent(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function persistToSession(snapshot) {
  try {
    if (!geciciTaslakHasContent(snapshot)) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...snapshot, savedAt: Date.now() }));
  } catch {
    /* quota / private mode */
  }
}

export function ServiceRecordWizardDraftProvider({ children }) {
  const [draft, setDraft] = useState(() => loadFromSession());
  const restorePendingRef = useRef(false);

  useEffect(() => {
    if (draft) persistToSession(draft);
    else {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
  }, [draft]);

  const saveDraft = useCallback((snapshot) => {
    if (!geciciTaslakHasContent(snapshot)) {
      setDraft(null);
      return;
    }
    setDraft({ ...snapshot, savedAt: Date.now() });
  }, []);

  const clearDraft = useCallback(() => {
    setDraft(null);
    restorePendingRef.current = false;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const hasDraft = useCallback(() => geciciTaslakHasContent(draft), [draft]);

  const requestRestore = useCallback(() => {
    if (!geciciTaslakHasContent(draft)) return false;
    restorePendingRef.current = true;
    return true;
  }, [draft]);

  const consumeRestorePending = useCallback(() => {
    if (!restorePendingRef.current) return false;
    restorePendingRef.current = false;
    return geciciTaslakHasContent(draft);
  }, [draft]);

  const markRestorePending = useCallback(() => {
    restorePendingRef.current = true;
  }, []);

  const getDraftSummary = useCallback(() => {
    if (!geciciTaslakHasContent(draft)) return null;
    const c = draft.meta?.selectedCustomer;
    const name = c ? `${c.adi || c.name || ''} ${c.soyadi || c.surname || ''}`.trim() : '';
    const tab = draft.meta?.activeTab || draft.wizard?.activeTab || '';
    return {
      musteriAdi: name || 'Müşteri seçili',
      adim: tab,
      isSantral: Boolean(draft.meta?.isSantralFlow),
    };
  }, [draft]);

  const value = useMemo(
    () => ({
      draft,
      saveDraft,
      clearDraft,
      hasDraft,
      requestRestore,
      consumeRestorePending,
      markRestorePending,
      getDraftSummary,
    }),
    [
      draft,
      saveDraft,
      clearDraft,
      hasDraft,
      requestRestore,
      consumeRestorePending,
      markRestorePending,
      getDraftSummary,
    ],
  );

  return (
    <ServiceRecordWizardDraftContext.Provider value={value}>
      {children}
    </ServiceRecordWizardDraftContext.Provider>
  );
}

export function useServiceRecordWizardDraft() {
  const ctx = useContext(ServiceRecordWizardDraftContext);
  if (!ctx) {
    throw new Error('useServiceRecordWizardDraft must be used within ServiceRecordWizardDraftProvider');
  }
  return ctx;
}

export function useServiceRecordWizardDraftOptional() {
  return useContext(ServiceRecordWizardDraftContext);
}

export default ServiceRecordWizardDraftContext;
